import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ProjectObject, ProjectRoleItem } from "@ServerTypes/projects/project.schema";
import type { SpaceUserItem, SpacesDataResponse } from "@ServerTypes/spaces/space.schema";
import type { CategoryItem, StatusItem } from "@ServerTypes/status/status.schema";
import type { StickySidebarIcon } from "@Components/StickySidebar/useStickySidebar";

/**
 * Every slice below used to be created with a bare `initialState: []`, which made its state type
 * `never[]` and every `action.payload` an untyped `any`. Each one now carries the element type of
 * the value its single dispatcher actually sends — the `z.infer` of the response field on the
 * server schema behind that call — so `RootState["roleData"]` &c. mean something.
 *
 * `action.payload ?? state` where the response field is optional: returning the draft is immer's
 * "no change", which is exactly what returning the old `undefined` payload did.
 */

/** The four fields `useStickySidebar` maps out of each icon before dispatching them. */
type SidebarIconNameEntry = Pick<StickySidebarIcon, "id" | "name" | "svg" | "toolTip">;

// Slice to store the list of roles within a project
// `getProjectRoles` -> `GetProjectRolesResponse["data"]` (actions/projectSetting.ts).
const initialRoleDataState: ProjectRoleItem[] = [];
const roleDataSlice = createSlice({
  name: "roleData",
  initialState: initialRoleDataState,
  reducers: {
    setProjectRolesList: (state, action: PayloadAction<ProjectRoleItem[] | undefined>) => action.payload ?? state,
  },
});
export const { setProjectRolesList } = roleDataSlice.actions;
export const roleData = roleDataSlice.reducer;

// Slice to store list of users inside spaces
// `getSpaceUsersList` -> `GetSpaceUsersListResponse["result"]` (design-viewer/DesignViewer.tsx).
const initialSpacesUsersDataState: SpaceUserItem[] = [];
const spacesUsersDataSlice = createSlice({
  name: "spacesUsersData",
  initialState: initialSpacesUsersDataState,
  reducers: {
    setSpacesInProjectList: (_state, action: PayloadAction<SpaceUserItem[]>) => action.payload,
  },
});
export const { setSpacesInProjectList } = spacesUsersDataSlice.actions;
export const spacesUsersData = spacesUsersDataSlice.reducer;

// Slice to store project entities list.
// `unknown[]`, not an element type: nothing in the app imports `setEntityListAction` and nothing
// reads `RootState["setEntityList"]`, so no dispatcher exists to take the element shape from.
const initialEntityListState: unknown[] = [];
const entityListSlice = createSlice({
  name: "entityList",
  initialState: initialEntityListState,
  reducers: {
    setEntityList: (_state, action: PayloadAction<unknown[]>) => action.payload,
  },
});
export const { setEntityList: setEntityListAction } = entityListSlice.actions;
export const setEntityList = entityListSlice.reducer;

// Slice to store status list
// `getStatusData` -> `GetStatusDataResponse["data"]` (actions/projectSetting.ts).
const initialStatusDataState: StatusItem[] = [];
const statusDataSlice = createSlice({
  name: "statusData",
  initialState: initialStatusDataState,
  reducers: {
    setStatusData: (state, action: PayloadAction<StatusItem[] | undefined>) => action.payload ?? state,
  },
});
export const { setStatusData } = statusDataSlice.actions;
export const statusData = statusDataSlice.reducer;

// Slice to store category list
// `getCategoryData` -> `GetCategoryDataResponse["data"]` (actions/projectSetting.ts).
const initialCategoryListState: CategoryItem[] = [];
const categoryListSlice = createSlice({
  name: "categoryList",
  initialState: initialCategoryListState,
  reducers: {
    setCategoryList: (state, action: PayloadAction<CategoryItem[] | undefined>) => action.payload ?? state,
  },
});
export const { setCategoryList: setCategoryListAction } = categoryListSlice.actions;
export const setCategoryList = categoryListSlice.reducer;

// Slice for deleted space status
// `removeSpace` -> `SpacesDataResponse["result"]`, an OBJECT (actions/projectSetting.ts). The slice
// is nevertheless seeded with the empty array it has always been seeded with, hence the union — a
// bare optional-property object type would reject `[]` under TypeScript's weak-type check.
type DeletedSpaceState = NonNullable<SpacesDataResponse["result"]> | never[];
// Seeded through `createSlice`'s lazy-initialiser overload, which is the only form that keeps the
// union: given a UNION-typed `initialState` VALUE, `State` inference collapses to its last
// constituent (`never[]`), and the reducer below — which returns the object arm — then fails to
// typecheck against `CaseReducer<never[], …>`. The initial value is the same `[]` as before.
const deleteSpaceSlice = createSlice({
  name: "deleteSpace",
  initialState: (): DeletedSpaceState => [],
  reducers: {
    deletedSpaceStatus: (state, action: PayloadAction<SpacesDataResponse["result"]>) => action.payload ?? state,
  },
});
export const { deletedSpaceStatus } = deleteSpaceSlice.actions;
export const deleteSpace = deleteSpaceSlice.reducer;

// Slice for deleted user status.
// `unknown[]` for the same reason as `entityList`: nothing imports `deletedUserStatus` and nothing
// reads `RootState["DeletedUserState"]`, so there is no dispatcher to take the element shape from.
const initialDeletedUserState: unknown[] = [];
const deletedUserSlice = createSlice({
  name: "DeletedUserState",
  initialState: initialDeletedUserState,
  reducers: {
    deletedUserStatus: (_state, action: PayloadAction<unknown[]>) => action.payload,
  },
});
export const { deletedUserStatus } = deletedUserSlice.actions;
export const DeletedUserState = deletedUserSlice.reducer;

// Slice for project setting object.
// State shape is the server `ProjectObject` (from `@ServerTypes/projects/project.schema`),
// the single source of truth — returned by `GET /projects/getProjectObject/:id`.
const initialProjectSettingObjectState: ProjectObject = {};
const projectSettingObjectSlice = createSlice({
  name: "getProjectSettingObject",
  initialState: initialProjectSettingObjectState,
  reducers: {
    getProjectSettingObject: (_state: ProjectObject, action: PayloadAction<ProjectObject | undefined>) =>
      action.payload ?? {},
  },
});

// `useStickySidebar` is the only dispatcher and sends `StickySidebarIcon` narrowed to four fields.
const initialSidebarIconNameState: SidebarIconNameEntry[] = [];
const projectSideBarIconAndNameMapper = createSlice({
  name: "projectSideBarIconsAndNameMapper",
  initialState: initialSidebarIconNameState,
  reducers: {
    setProjectSideBarIconsAndNameMapper: (_state, action: PayloadAction<SidebarIconNameEntry[]>) => action.payload,
  },
});
export const { setProjectSideBarIconsAndNameMapper } = projectSideBarIconAndNameMapper.actions;
export const projectSideBarIconAndNameMapperReducer = projectSideBarIconAndNameMapper.reducer;
export const { getProjectSettingObject: getProjectSettingObjectAction } = projectSettingObjectSlice.actions;
export const getProjectSettingObject = projectSettingObjectSlice.reducer;
